import React, { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { templateActionEnum, ROOT_PATH } from "../../Config/Constants";
import TemplateDataContext from "../../Contexts/TemplateData.context";
import Footer from "../Footer/Footer";
import ToastComponent from "../Shared/ToastComponent";
import TemplateHeader from "../Header/TemplateHeader";
import ExpandableSidebar from "../Shared/ExpandableSidebar";
import HorizontalTipsBar from "../Shared/HorizontalTipsBar";
import ToolTipSideBar from "../Shared/ToolTipSideBar";

type Props = {
  mainComponent: React.ReactNode;
};

const TemplateLayout = ({ mainComponent }: Props) => {
  const { state, dispatch } = useContext(TemplateDataContext);
  const { show, message } = state.error;
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Handle window resize to determine mobile vs desktop
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 991.98);
    };

    // Check on mount
    checkIsMobile();

    // Add resize listener
    window.addEventListener("resize", checkIsMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const handleShow = () => setSideNavOpen(true);
  const handleClose = () => setSideNavOpen(false);

  // Check if we're in the template selection phase (LoadTemplate component)
  // LoadTemplate routes: /app/template/:templateType and /app/template/state-specific/:templateType/:state
  const isTemplateSelection = () => {
    const path = location.pathname;

    // Template selection routes (LoadTemplate component):
    // /app/template/:templateType (matches exactly 3 segments)
    // /app/template/state-specific/:templateType/:state (matches exactly 5 segments)
    const segments = path.split("/").filter(Boolean);

    if (
      segments.length === 3 &&
      segments[0] === "app" &&
      segments[1] === "template"
    ) {
      return true; // /app/template/:templateType
    }

    if (
      segments.length === 5 &&
      segments[0] === "app" &&
      segments[1] === "template" &&
      segments[2] === "state-specific"
    ) {
      return true; // /app/template/state-specific/:templateType/:state
    }

    return false;
  };

  return (
    <>
      <ToastComponent
        show={show || false}
        message={message || ""}
        closeHandler={() =>
          dispatch({
            type: templateActionEnum.SET_ERROR,
            payload: { show: false },
          })
        }
      />
      {/* Template Header */}
      <TemplateHeader
        handleShow={handleShow}
        handleClose={handleClose}
        isOpen={sideNavOpen}
        hideActions={isTemplateSelection()}
      />

      {/* Main content area with sidebar */}
      <div style={{ position: "relative" }}>
        <div
          className="container-fluid"
          style={{
            paddingRight: !isTemplateSelection() ? "0px" : "0",
          }}
        >
          {mainComponent}
        </div>

        {/* Expandable Sidebar or Horizontal Tips Bar - Show when NOT in template selection */}
        {!isTemplateSelection() && (
          <>
            {/* Always mount both; CSS decides visibility (desktop swap at 1320px) */}
            {!isMobile && <ExpandableSidebar />}
            <HorizontalTipsBar />
          </>
        )}
      </div>

      {/* Footer */}
      {/* Uncomment the footer if needed. Commented following figma design*/}
      <div className="temp-footer">
        <Footer />
      </div>
    </>
  );
};

export default TemplateLayout;
